import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw,
  Linkedin,
  Twitter,
  Mail,
  Check,
  X,
  Copy,
  ExternalLink,
  Filter,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Database,
  AlertCircle,
  ChevronRight,
  SkipForward,
  Send,
  Inbox,
  Archive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_ICONS = {
  linkedin: Linkedin,
  x: Twitter,  // v1.2.1: Standardized to 'x'
  twitter: Twitter,  // Legacy support
  email: Mail,
};

const PLATFORM_COLORS = {
  linkedin: "bg-blue-600/20 text-blue-400",
  x: "bg-sky-500/20 text-sky-400",  // v1.2.1: Standardized to 'x'
  twitter: "bg-sky-500/20 text-sky-400",  // Legacy support
  email: "bg-amber-500/20 text-amber-400",
};

// v1.2.0: Workflow status config
const WORKFLOW_STATUS_CONFIG = {
  pending: { icon: Clock, color: "bg-zinc-500/20 text-zinc-400", label: "Pending" },
  opened: { icon: ExternalLink, color: "bg-yellow-500/20 text-yellow-400", label: "Opened" },
  posted: { icon: CheckCircle2, color: "bg-green-500/20 text-green-400", label: "Posted" },
  skipped: { icon: SkipForward, color: "bg-zinc-600/20 text-zinc-500", label: "Skipped" },
};

const LAST_OPENED_KEY = 'distribution_last_opened_draft';

export default function DistributionAdmin() {
  const [unsubmittedDrafts, setUnsubmittedDrafts] = useState([]);
  const [completedDrafts, setCompletedDrafts] = useState([]);
  const [workflowStats, setWorkflowStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("unsubmitted");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [authToken, setAuthToken] = useState(localStorage.getItem('auth_token') || '');
  
  // v1.2.0: Post confirmation modal
  const [showPostConfirm, setShowPostConfirm] = useState(false);
  const [confirmDraftId, setConfirmDraftId] = useState(null);
  const [postedUrl, setPostedUrl] = useState('');
  const [postNote, setPostNote] = useState('');

  // Auth header helper
  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${authToken}`
  }), [authToken]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const params = platformFilter !== 'all' ? `?platform=${platformFilter}` : '';
      
      const [unsubRes, compRes, statsRes] = await Promise.all([
        axios.get(`${API}/distribution/unsubmitted${params}`, { headers }),
        axios.get(`${API}/distribution/completed${params}`, { headers }),
        axios.get(`${API}/distribution/workflow-stats`, { headers })
      ]);

      setUnsubmittedDrafts(unsubRes.data.drafts || []);
      setCompletedDrafts(compRes.data.drafts || []);
      setWorkflowStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Please log in as admin");
      }
    } finally {
      setLoading(false);
    }
  }, [platformFilter, getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // v1.2.0: Handle window focus - check if user returned from posting
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastOpened = localStorage.getItem(LAST_OPENED_KEY);
        if (lastOpened) {
          const { draftId, channel, timestamp } = JSON.parse(lastOpened);
          // Only show modal if opened within last 30 minutes
          const thirtyMinsAgo = Date.now() - (30 * 60 * 1000);
          if (timestamp > thirtyMinsAgo) {
            setConfirmDraftId(draftId);
            setShowPostConfirm(true);
          } else {
            localStorage.removeItem(LAST_OPENED_KEY);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    
    // Check on mount too
    handleVisibilityChange();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  // v1.2.0: Share to social (opens in new tab, marks as opened)
  const handleShare = async (draft, channel) => {
    try {
      const headers = getAuthHeaders();
      
      // Get share URLs
      const urlsRes = await axios.get(`${API}/distribution/drafts/${draft.id}/share-urls`, { headers });
      const urls = urlsRes.data;
      
      // Mark as opened
      await axios.post(`${API}/distribution/drafts/${draft.id}/opened?channel=${channel}`, {}, { headers });
      
      // Store in localStorage for return detection
      localStorage.setItem(LAST_OPENED_KEY, JSON.stringify({
        draftId: draft.id,
        channel,
        timestamp: Date.now()
      }));
      
      // Open the share URL
      const shareUrl = channel === 'linkedin' ? urls.linkedin : urls.twitter;
      window.open(shareUrl, '_blank');
      
      toast.info(`Opening ${channel}... Come back to confirm when posted`);
      
      // Refresh data
      await fetchData();
      
    } catch (error) {
      console.error("Share error:", error);
      toast.error("Failed to share");
    }
  };

  // v1.2.0: Confirm posted
  const handleConfirmPosted = async () => {
    if (!confirmDraftId) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/distribution/drafts/${confirmDraftId}/posted`,
        { posted_url: postedUrl || null, note: postNote || null },
        { headers }
      );
      
      toast.success("Marked as posted!");
      localStorage.removeItem(LAST_OPENED_KEY);
      setShowPostConfirm(false);
      setConfirmDraftId(null);
      setPostedUrl('');
      setPostNote('');
      
      // Auto-advance to next if available
      if (response.data.next_pending_id) {
        const nextDraft = unsubmittedDrafts.find(d => d.id === response.data.next_pending_id);
        if (nextDraft) {
          setSelectedDraft(nextDraft);
        }
      }
      
      await fetchData();
      
    } catch (error) {
      toast.error("Failed to mark as posted");
    }
  };

  // v1.2.0: Mark as skipped
  const handleSkip = async (draftId, note = null) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/distribution/drafts/${draftId}/skipped`,
        { note },
        { headers }
      );
      
      toast.info("Draft skipped");
      localStorage.removeItem(LAST_OPENED_KEY);
      setShowPostConfirm(false);
      setConfirmDraftId(null);
      
      // Auto-advance
      if (response.data.next_pending_id) {
        const nextDraft = unsubmittedDrafts.find(d => d.id === response.data.next_pending_id);
        if (nextDraft) {
          setSelectedDraft(nextDraft);
        }
      }
      
      await fetchData();
      
    } catch (error) {
      toast.error("Failed to skip");
    }
  };

  // v1.2.0: Not yet (keep as opened, dismiss modal)
  const handleNotYet = () => {
    setShowPostConfirm(false);
    // Keep localStorage so we ask again on next focus
  };

  const copyToClipboard = async (text, draft = null) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
    
    // If draft provided, could optionally mark as opened for email
    if (draft && draft.platform === 'email') {
      try {
        const headers = getAuthHeaders();
        await axios.post(`${API}/distribution/drafts/${draft.id}/opened?channel=email`, {}, { headers });
        await fetchData();
      } catch (e) {
        // Ignore
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'n' || e.key === 'N') {
        // Next draft (only if not in modal/dialog)
        if (!showPostConfirm && !selectedDraft) {
          const pendingDrafts = unsubmittedDrafts.filter(d => d.workflow_status === 'pending');
          if (pendingDrafts.length > 0) {
            setSelectedDraft(pendingDrafts[0]);
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPostConfirm, selectedDraft, unsubmittedDrafts]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const renderDraftCard = (draft, showActions = true) => {
    const PlatformIcon = PLATFORM_ICONS[draft.platform] || FileText;
    const workflowStatus = draft.workflow_status || 'pending';
    const statusConfig = WORKFLOW_STATUS_CONFIG[workflowStatus] || WORKFLOW_STATUS_CONFIG.pending;
    const StatusIcon = statusConfig.icon;
    
    return (
      <div
        key={draft.id}
        className="border border-zinc-800 rounded-lg p-4 hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedDraft(draft)}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${PLATFORM_COLORS[draft.platform]}`}>
                <PlatformIcon className="h-3 w-3" />
                {draft.platform}
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono ${statusConfig.color}`}>
                <StatusIcon className="h-3 w-3" />
                {statusConfig.label}
              </span>
            </div>
            <div className="text-white font-medium truncate">{draft.page_title}</div>
            <div className="text-zinc-500 text-sm truncate">{draft.domain}/{draft.slug}</div>
            <div className="text-zinc-600 text-xs mt-1">{formatDate(draft.created_at)}</div>
          </div>
          
          {showActions && workflowStatus !== 'posted' && workflowStatus !== 'skipped' && (
            <div className="flex items-center gap-1">
              {draft.platform === 'linkedin' && (
                <Button
                  size="sm"
                  className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={(e) => { e.stopPropagation(); handleShare(draft, 'linkedin'); }}
                >
                  <Linkedin className="h-4 w-4 mr-1" />
                  Post
                </Button>
              )}
              {(draft.platform === 'x' || draft.platform === 'twitter') && (
                <Button
                  size="sm"
                  className="h-8 bg-sky-500 hover:bg-sky-600 text-white"
                  onClick={(e) => { e.stopPropagation(); handleShare(draft, 'x'); }}
                >
                  <Twitter className="h-4 w-4 mr-1" />
                  Post
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-zinc-400"
                onClick={(e) => { e.stopPropagation(); copyToClipboard(draft.copy, draft); }}
                title="Copy text"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-zinc-500"
                onClick={(e) => { e.stopPropagation(); handleSkip(draft.id); }}
                title="Skip"
              >
                <SkipForward className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="distribution-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-medium text-white">Distribution Queue</h1>
          <p className="text-sm text-zinc-500">v1.2.0 Manual Posting Workflow</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Workflow Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 text-xs text-zinc-500 uppercase">
              <Inbox className="h-3 w-3" />
              Unsubmitted
            </div>
            <div className="font-mono text-xl text-amber-400">{workflowStats?.unsubmitted_count || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 text-xs text-zinc-500 uppercase">
              <Archive className="h-3 w-3" />
              Completed
            </div>
            <div className="font-mono text-xl text-green-400">{workflowStats?.completed_count || 0}</div>
          </CardContent>
        </Card>
        {Object.entries(workflowStats?.by_channel || {}).map(([channel, count]) => {
          const Icon = PLATFORM_ICONS[channel] || FileText;
          return (
            <Card key={channel} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-3">
                <div className="flex items-center gap-1 text-xs text-zinc-500 uppercase">
                  <Icon className="h-3 w-3" />
                  Posted to {channel}
                </div>
                <div className="font-mono text-xl text-white">{count}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Platform Filter */}
      <div className="flex items-center gap-3">
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="x">X (Twitter)</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500">Press N for next draft</span>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-800">
          <TabsTrigger value="unsubmitted" className="data-[state=active]:bg-zinc-700">
            <Inbox className="h-4 w-4 mr-1" />
            Unsubmitted ({unsubmittedDrafts.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-zinc-700">
            <Archive className="h-4 w-4 mr-1" />
            Completed ({completedDrafts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="unsubmitted" className="mt-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="space-y-3">
                {unsubmittedDrafts.map(draft => renderDraftCard(draft, true))}
                {unsubmittedDrafts.length === 0 && (
                  <div className="py-8 text-center text-zinc-500">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    All caught up! No pending drafts.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="space-y-3">
                {completedDrafts.map(draft => renderDraftCard(draft, false))}
                {completedDrafts.length === 0 && (
                  <div className="py-8 text-center text-zinc-500">
                    No completed drafts yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Draft Detail Modal */}
      <Dialog open={!!selectedDraft} onOpenChange={() => setSelectedDraft(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              {selectedDraft && PLATFORM_ICONS[selectedDraft.platform] && (
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded ${PLATFORM_COLORS[selectedDraft.platform]}`}>
                  {(() => {
                    const Icon = PLATFORM_ICONS[selectedDraft.platform];
                    return <Icon className="h-4 w-4" />;
                  })()}
                </span>
              )}
              {selectedDraft?.page_title}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedDraft?.domain}/{selectedDraft?.slug}
            </DialogDescription>
          </DialogHeader>

          {selectedDraft && (
            <div className="space-y-4">
              {/* Share Actions */}
              {(selectedDraft.workflow_status === 'pending' || selectedDraft.workflow_status === 'opened') && (
                <div className="flex items-center gap-2 pb-4 border-b border-zinc-700">
                  {selectedDraft.platform === 'linkedin' && (
                    <Button
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleShare(selectedDraft, 'linkedin')}
                    >
                      <Linkedin className="h-4 w-4 mr-2" />
                      Post on LinkedIn
                    </Button>
                  )}
                  {(selectedDraft.platform === 'x' || selectedDraft.platform === 'twitter') && (
                    <Button
                      className="bg-sky-500 hover:bg-sky-600"
                      onClick={() => handleShare(selectedDraft, 'x')}
                    >
                      <Twitter className="h-4 w-4 mr-2" />
                      Post on X
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(selectedDraft.copy, selectedDraft)}
                    className="border-zinc-600"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Text
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    onClick={() => { handleSkip(selectedDraft.id); setSelectedDraft(null); }}
                    className="text-zinc-500"
                  >
                    <SkipForward className="h-4 w-4 mr-2" />
                    Skip
                  </Button>
                </div>
              )}

              {/* Content */}
              <div className="space-y-2">
                <h4 className="text-xs text-zinc-500 uppercase tracking-wider">Content</h4>
                <Textarea
                  value={selectedDraft.copy}
                  readOnly
                  className="bg-zinc-800 border-zinc-700 text-zinc-200 min-h-[200px] font-mono text-sm"
                />
              </div>

              {/* Target URL */}
              <div className="space-y-2">
                <h4 className="text-xs text-zinc-500 uppercase tracking-wider">Target URL</h4>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-zinc-800 p-2 rounded text-xs text-emerald-400 overflow-x-auto">
                    {selectedDraft.target_url}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(selectedDraft.target_url, '_blank')}
                    className="text-zinc-400"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Posted info */}
              {selectedDraft.workflow_status === 'posted' && (
                <div className="bg-green-900/20 border border-green-700/30 rounded p-3 space-y-1">
                  <div className="text-green-400 text-sm font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Posted
                  </div>
                  <div className="text-xs text-zinc-400">
                    By {selectedDraft.posted_by_username} on {formatDate(selectedDraft.posted_at)}
                    {selectedDraft.posted_channel && ` via ${selectedDraft.posted_channel}`}
                  </div>
                  {selectedDraft.posted_url && (
                    <a href={selectedDraft.posted_url} target="_blank" rel="noopener noreferrer" 
                       className="text-xs text-blue-400 hover:underline">
                      View post →
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post Confirmation Modal */}
      <Dialog open={showPostConfirm} onOpenChange={setShowPostConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-400" />
              Did you post?
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              You opened a share link. Let us know if you posted it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs text-zinc-500 uppercase">Post URL (optional)</label>
              <Input
                placeholder="https://linkedin.com/posts/..."
                value={postedUrl}
                onChange={(e) => setPostedUrl(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs text-zinc-500 uppercase">Note (optional)</label>
              <Input
                placeholder="Any notes..."
                value={postNote}
                onChange={(e) => setPostNote(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleNotYet}
              className="text-zinc-400"
            >
              Not yet
            </Button>
            <Button
              variant="outline"
              onClick={() => { handleSkip(confirmDraftId); }}
              className="border-zinc-600 text-zinc-300"
            >
              <SkipForward className="h-4 w-4 mr-2" />
              Skip
            </Button>
            <Button
              onClick={handleConfirmPosted}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Yes, posted!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
